/* ============================================================
   xlsx.js — just enough of an .xlsx reader to pull a list of
   player names out of a club export. An .xlsx is a zip of XML
   files; the browser's DecompressionStream inflates it, so no
   spreadsheet library is needed.

   The name column is found by its header ("Namn", or "Förnamn" +
   "Efternamn"); without a header the first column is used.
   ============================================================ */

const NAME_HEADERS = ['namn', 'name', 'spelare', 'spelarnamn', 'fullständigt namn'];
const FIRST_HEADERS = ['förnamn', 'fornamn', 'first name'];
const LAST_HEADERS = ['efternamn', 'last name'];

/** Read the zip's central directory: { path: () => Promise<string> }. */
function unzip(buf) {
  const view = new DataView(buf);
  let eocd = -1;
  for (let i = buf.byteLength - 22; i >= Math.max(0, buf.byteLength - 65557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('Filen är inte en Excel-fil (.xlsx).');

  const files = {};
  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const utf8 = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (view.getUint32(p, true) !== 0x02014b50) break;
    const method = view.getUint16(p + 10, true);
    const size = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const local = view.getUint32(p + 42, true);
    const name = utf8.decode(new Uint8Array(buf, p + 46, nameLen));
    files[name] = async () => {
      const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
      const data = new Uint8Array(buf, start, size);
      if (method === 0) return utf8.decode(data);
      if (method !== 8) throw new Error('Excel-filen använder en komprimering som inte stöds.');
      const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      return new Response(stream).text();
    };
    p += 46 + nameLen + extraLen + commentLen;
  }
  return files;
}

const xml = s => new DOMParser().parseFromString(s, 'application/xml');
const byTag = (node, tag) => [...node.getElementsByTagNameNS('*', tag)];
const textOf = node => byTag(node, 't').map(t => t.textContent).join('');

/** 'C12' → 2 */
function colIndex(ref) {
  let n = 0;
  for (const ch of ref.replace(/\d+$/, '').toUpperCase()) n = n * 26 + ch.charCodeAt(0) - 64;
  return n - 1;
}

/** The first worksheet in tab order, as rows of strings. */
async function firstSheetRows(files) {
  const read = async path => (files[path] ? xml(await files[path]()) : null);
  const workbook = await read('xl/workbook.xml');
  const rels = await read('xl/_rels/workbook.xml.rels');
  if (!workbook) throw new Error('Filen är inte en Excel-fil (.xlsx).');

  let path = 'xl/worksheets/sheet1.xml';
  const first = byTag(workbook, 'sheet')[0];
  const rid = first && [...first.attributes].find(a => a.localName === 'id')?.value;
  const target = rid && rels && byTag(rels, 'Relationship').find(r => r.getAttribute('Id') === rid)?.getAttribute('Target');
  if (target) path = target.startsWith('/') ? target.slice(1) : 'xl/' + target.replace(/^\.\//, '');

  const sheet = await read(path);
  if (!sheet) throw new Error('Hittade inget kalkylblad i filen.');
  const sst = await read('xl/sharedStrings.xml');
  const shared = sst ? byTag(sst, 'si').map(textOf) : [];

  return byTag(sheet, 'row').map(row => {
    const cells = [];
    byTag(row, 'c').forEach((c, i) => {
      const at = c.getAttribute('r') ? colIndex(c.getAttribute('r')) : i;
      const type = c.getAttribute('t');
      const v = byTag(c, 'v')[0]?.textContent ?? '';
      cells[at] = type === 's' ? (shared[+v] ?? '') : type === 'inlineStr' ? textOf(c) : v;
    });
    return Array.from(cells, v => String(v ?? '').trim());
  });
}

/** Pick the name out of each row below the header. */
export function namesFromRows(rows) {
  const norm = s => s.toLowerCase().replace(/\s+/g, ' ').trim();
  const find = (row, list) => row.findIndex(c => list.includes(norm(c || '')));

  let header = -1, nameCol = 0, firstCol = -1, lastCol = -1;
  for (let r = 0; r < Math.min(rows.length, 10) && header < 0; r++) {
    const row = rows[r];
    if (find(row, NAME_HEADERS) >= 0) { header = r; nameCol = find(row, NAME_HEADERS); }
    else if (find(row, FIRST_HEADERS) >= 0 && find(row, LAST_HEADERS) >= 0) {
      header = r; firstCol = find(row, FIRST_HEADERS); lastCol = find(row, LAST_HEADERS);
    }
  }

  const seen = new Set();
  const names = [];
  for (const row of rows.slice(header + 1)) {
    const name = (firstCol >= 0 ? `${row[firstCol] || ''} ${row[lastCol] || ''}` : row[nameCol] || '')
      .replace(/\s+/g, ' ').trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    names.push(name);
  }
  return names;
}

/** Read a File from an <input type="file"> and return the player names in it. */
export async function readPlayerNames(file) {
  if (!/\.xlsx$/i.test(file.name)) throw new Error('Välj en Excel-fil i formatet .xlsx.');
  return namesFromRows(await firstSheetRows(unzip(await file.arrayBuffer())));
}
